/*
 * <BeBox> -- the R5 group box (components/box.scss). A <fieldset>/<legend> when
 * labelled, a <div> when not, and a pressable card given `onClick` or `href`.
 */

import { type HTMLAttributes, type MouseEventHandler, type ReactNode, useId } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeBoxBorder = "fancy" | "plain" | "none";

/* HTMLAttributes rather than one element's: the root is a fieldset, div, button or
 * a, depending on mode. */
export type BeBoxProps = BeCommon &
	Omit<HTMLAttributes<HTMLElement>, "className" | "style" | "onClick"> & {
		/** Notched into the top edge. Any content, as R5's SetLabel(BView*). */
		label?: ReactNode;
		/** A right-aligned slot over the top edge. Not R5. */
		accessory?: ReactNode;
		/** "fancy" (default) is the etched groove, "plain" a single bevel. */
		border?: BeBoxBorder;
		/** Take the block size the parent gives it, so boxes in a grid row match. */
		fill?: boolean;
		/** No inner padding, for content that draws to its own edge. */
		flush?: boolean;
		children?: ReactNode;
		/** Makes it a card: a <button> that inverts when pressed, as BButton does.
		 * Drops `label`, which needs a <legend>. Not R5. */
		onClick?: MouseEventHandler<HTMLElement>;
		/** As `onClick`, rendered as an <a>. */
		href?: string;
		/** The card a detail pane is showing: `aria-current`, since `aria-selected`
		 * is inert on a <button>. */
		current?: boolean;
	};

export const BeBox = beComponent<BeBoxProps, HTMLElement>("BeBox", (props, be) => {
	const { label, accessory, border = "fancy", fill, flush, children, onClick, href, current, scale, className, style, ...rest } = props;

	const titleId = useId();
	const card = onClick != null || href != null;
	const root = be(
		"be-box",
		border === "plain" && "is-plain",
		border === "none" && "is-borderless",
		// a card IS a surface, so it takes .be-panel's fill and states (Notes/73 §2)
		card && "be-panel is-card",
		fill && "is-fill",
		flush && "is-flush",
	);

	const accessorySlot = accessory != null ? <span className="be-box__accessory">{accessory}</span> : null;

	if (card) {
		const content = (
			<>
				{accessorySlot}
				{children}
			</>
		);
		// undefined, not "false": that would announce "not current" on every card
		const currentAttr = current ? "true" : undefined;
		return href != null ? (
			<a {...rest} {...root} href={href} onClick={onClick} aria-current={currentAttr}>
				{content}
			</a>
		) : (
			<button type="button" {...rest} {...root} onClick={onClick} aria-current={currentAttr}>
				{content}
			</button>
		);
	}

	if (label != null) {
		return (
			// aria-labelledby, so an accessory sharing the legend stays out of the
			// group's accessible name
			<fieldset {...rest} {...root} aria-labelledby={titleId}>
				<legend className="be-box__label">
					{/* the title paints a background to notch the border, and
					    .be-pixel-text must not sit on a background-painting element */}
					<span id={titleId} className="be-box__title be-text--tab">
						{pixelText(label)}
					</span>
					{accessorySlot}
				</legend>
				{children}
			</fieldset>
		);
	}
	return (
		<div {...rest} {...root}>
			{accessorySlot}
			{children}
		</div>
	);
});
